/* Text drafts live apart from the message snapshot so its seven-day expiry and
 * 200-chat limit cannot silently remove unsent text. One record belongs to one
 * account; chat IDs are the keys inside it. */
const DATABASE = 'andresot-drafts';
const STORE = 'drafts';
let writes: Promise<unknown> = Promise.resolve();

function open(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DATABASE, 1);
		request.onupgradeneeded = () => request.result.createObjectStore(STORE);
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
		request.onblocked = () => reject(new Error('Хранилище черновиков занято'));
	});
}

async function access<T>(
	mode: IDBTransactionMode,
	work: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
	const db = await open();
	try {
		return await new Promise<T>((resolve, reject) => {
			const transaction = db.transaction(STORE, mode);
			const request = work(transaction.objectStore(STORE));
			transaction.oncomplete = () => resolve(request.result);
			request.onerror = () => reject(request.error);
			transaction.onabort = () => reject(transaction.error);
		});
	} finally {
		db.close();
	}
}

/** Null means storage is unavailable; an empty object means there are no drafts. */
export async function readDrafts(userId: string): Promise<Record<string, string> | null> {
	if (typeof indexedDB === 'undefined') return null;
	try {
		await writes;
		return (await access('readonly', (store) => store.get(userId))) ?? {};
	} catch {
		return null;
	}
}

function enqueue(work: () => Promise<void>): Promise<boolean> {
	const result = writes.then(work, work).then(
		() => true,
		() => false
	);
	writes = result;
	return result;
}

/** Serialise full-record writes so a slow earlier edit cannot overwrite a later one. */
export function writeDrafts(userId: string, drafts: Record<string, string>): Promise<boolean> {
	const saved = { ...drafts };
	return enqueue(async () => {
		await access('readwrite', (store) => store.put(saved, userId));
	});
}

export function clearDrafts(userId: string): Promise<boolean> {
	return enqueue(async () => {
		await access('readwrite', (store) => store.delete(userId));
	});
}

export function clearAllDrafts(): Promise<boolean> {
	return enqueue(async () => {
		await access('readwrite', (store) => store.clear());
	});
}
